import { useState } from 'react';
import { TaxResult } from '../../../utils/TaxEngine';
import { TaxEngine } from '../../../utils/TaxEngine';

interface TaxVisualizationProps {
  taxResult: TaxResult;
  locationData: any;
  formatCurrency: (amount: number) => string;
}

const TaxVisualization = ({ taxResult, locationData, formatCurrency }: TaxVisualizationProps) => {
  const [activeTab, setActiveTab] = useState<'simple' | 'detailed' | 'brackets'>('simple');

  const getChartData = () => {
    const data = [
      { label: 'Net Pay', amount: taxResult.netSalary, color: 'bg-blue-500' },
      ...taxResult.breakdown.map(item => ({
        label: item.category,
        amount: item.amount,
        color: getCategoryColor(item.category)
      }))
    ];
    return data;
  };

  const chartData = getChartData();
  const total = taxResult.grossSalary;

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Where Your Money Goes in {locationData.locationName}
        </h2>

        {/* Bar Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="mb-8">
            <div className="h-16 flex rounded-lg overflow-hidden">
              {chartData.map((item, index) => {
                const percentage = (item.amount / total) * 100;
                return (
                  <div
                    key={index}
                    className={`${item.color} relative group cursor-pointer transition-all hover:opacity-90`}
                    style={{ width: `${percentage}%` }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      {percentage.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {chartData.map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded ${item.color}`}></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.label}</p>
                  <p className="text-xs text-gray-600">{formatCurrency(item.amount)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabbed Breakdown */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-8">
              <button
                onClick={() => setActiveTab('simple')}
                className={`py-4 text-sm font-medium border-b-3 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'simple'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Simple View
              </button>
              <button
                onClick={() => setActiveTab('detailed')}
                className={`py-4 text-sm font-medium border-b-3 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'detailed'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Detailed Breakdown
              </button>
              <button
                onClick={() => setActiveTab('brackets')}
                className={`py-4 text-sm font-medium border-b-3 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'brackets'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Tax Brackets
              </button>
            </div>
          </div>

          <div className="p-8">
            {activeTab === 'simple' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Tax Type</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Rate</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxResult.breakdown.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="py-3 px-4 text-sm text-gray-900">{item.category}</td>
                        <td className="py-3 px-4 text-sm text-gray-600 text-right">
                          {(item.rate * 100).toFixed(2)}%
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold text-gray-900 text-right">
                          {formatCurrency(item.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-900 text-white font-bold">
                      <td className="py-4 px-4 text-sm">Total Tax</td>
                      <td className="py-4 px-4 text-sm text-right">
                        {(taxResult.effectiveRate * 100).toFixed(2)}%
                      </td>
                      <td className="py-4 px-4 text-sm text-right">
                        {formatCurrency(taxResult.totalTax)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'detailed' && (
              <div className="space-y-4">
                {taxResult.breakdown.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{item.category}</h4>
                      <span className="text-lg font-bold text-gray-900">{formatCurrency(item.amount)}</span>
                    </div>
                    {item.details && (
                      <p className="text-sm text-gray-600 pl-4 border-l-2 border-blue-500">
                        {item.details}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'brackets' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Understanding Tax Brackets:</strong> Your income is taxed progressively, meaning different portions are taxed at different rates. You don't pay the highest rate on all your income.
                  </p>
                </div>
                <div className="space-y-3">
                  {getBracketInfo(locationData, taxResult.grossSalary).map((bracket, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        bracket.isActive
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(bracket.min)} - {bracket.max === 999999999 ? '∞' : formatCurrency(bracket.max)}
                          </p>
                          <p className="text-sm text-gray-600">Tax Rate: {(bracket.rate * 100).toFixed(1)}%</p>
                        </div>
                        {bracket.isActive && (
                          <span className="px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                            Your Bracket
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const getCategoryColor = (category: string): string => {
  const colors: { [key: string]: string } = {
    'Federal Tax': 'bg-red-500',
    'State Tax': 'bg-orange-500',
    'Local Tax': 'bg-yellow-500',
    'Social Security': 'bg-purple-500',
    'Medicare': 'bg-pink-500',
    'Income Tax': 'bg-red-500',
    'National Insurance': 'bg-purple-500'
  };
  return colors[category] || 'bg-gray-500';
};

const getBracketInfo = (locationData: any, salary: number) => {
  const brackets = locationData.country.federal_tax?.brackets || locationData.country.income_tax?.brackets || [];
  return brackets.map((bracket: any) => ({
    ...bracket,
    isActive: salary >= bracket.min && salary <= bracket.max
  }));
};

export default TaxVisualization;